// Pure helpers behind "drop a crew member onto a stage row" in the schedule
// creator. Kept separate from CrewSchedule so the slot-matching and end-time
// maths are unit-testable without the drag/DOM machinery.

export type SetSlot = { set_start: string; set_end: string };

// Compare clock times by minute so a DB "18:20:00" and a UI "18:20" match.
// Without this, string comparison treats "18:20" as < "18:20:00", so a drop
// exactly on a set start was read as *before* the set and fell through to the
// "new custom subject" dialog instead of creating an artist assignment.
function clockMinutes(time: string): number {
  const [h = "0", m = "0"] = time.split(":");
  return Number(h) * 60 + Number(m);
}

// The artist set that contains `dropTime` (inclusive of both ends), or null
// when the drop lands on an empty stretch of the stage — e.g. an empty
// stage, or a gap before any DJ is scheduled.
//
// When one set ends exactly where the next begins (e.g. 21:30) the drop time
// belongs to BOTH. Prefer the set that STARTS there: dropping on 21:30 means
// you want to film the artist coming on, not the one that just finished (whose
// slot would then clamp the block back into its final quarter). Otherwise the
// first containing set (input order) wins, so genuinely overlapping sets stay
// deterministic.
export function artistAtTime<T extends SetSlot>(slots: T[], dropTime: string): T | null {
  const t = clockMinutes(dropTime);
  const containing = slots.filter(
    (a) => t >= clockMinutes(a.set_start) && t <= clockMinutes(a.set_end),
  );
  if (containing.length === 0) return null;
  return containing.find((a) => clockMinutes(a.set_start) === t) ?? containing[0];
}

// "HH:MM" + N minutes, wrapping past midnight back into a 24h clock so the
// custom-assignment dialog always gets a valid end time.
export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + minutes;
  const wrapped = ((total % 1440) + 1440) % 1440;
  const hh = Math.floor(wrapped / 60);
  const mm = wrapped % 60;
  return `${hh.toString().padStart(2, "0")}:${mm.toString().padStart(2, "0")}`;
}
